import { createApi } from '@reduxjs/toolkit/query/react';

import { createGraphQLFetchBaseQuery } from '@/shared/redux/api/utils/graphql-fetch-base-query';

export const API_URL = import.meta.env.VITE_APP_SDA_API_URL;

export const API_URLS = {
  ASSETS: `${API_URL}/assets/graphql/v1`,
  IDENT: `${API_URL}/ident/graphql/v1`,
  IDENT_PUBLIC: `${API_URL}/ident/graphql/v1/public`,
  JOBS: `${API_URL}/jobs/graphql/v1`,
  IDEAAS: `${API_URL}/ideaas/graphql/v1`,
  VSCODE: `${API_URL}/vscode/graphql/v1`,
  OPENPLC: `${API_URL}/openplc/graphql/v1`,
  SDAI: `${API_URL}/sdai/graphql/v1`,
  NOTIFICATIONS: `${API_URL}/notifications/graphql/v1`,
  OFFBOARDING: `${API_URL}/offboarding/graphql/v1`,
  AUDIT: `${API_URL}/audit/graphql/v1`,
  INTEGRATIONS: `${API_URL}/integration/graphql/v1`,
  BILLING: `${API_URL}/billing/graphql/v1`,
  IDEAAS_V2: `${API_URL}/ideaas-v2/graphql/v1`,
  TELEMETRY: `${API_URL}/telemetry/graphql/v1`,
} as const;

export const LEGACY_API_URLS = {
  ASSETS: `${API_URL}/assets/v1/graphql`,
  IDENT: `${API_URL}/ident/v1/graphql`,
  IDENT_PUBLIC: `${API_URL}/ident/v1/public/graphql`,
  JOBS: `${API_URL}/jobs/v1/graphql`,
  IDEAAS: `${API_URL}/ideaas/v1/graphql`,
  VSCODE: `${API_URL}/vscode/v1/graphql`,
  OPENPLC: `${API_URL}/openplc/v1/graphql`,
  SDAI: `${API_URL}/sdai/v1/graphql`,
  NOTIFICATIONS: `${API_URL}/notifications/v1/graphql`,
  OFFBOARDING: `${API_URL}/offboarding/v1/graphql`,
  AUDIT: `${API_URL}/audit/v1/graphql`,
  INTEGRATIONS: `${API_URL}/integration/v1/graphql`,
  BILLING: `${API_URL}/billing/v1/graphql`,
  TELEMETRY: `${API_URL}/telemetry/v1/graphql`,
} as const;

const createBaseApi = (
  url: string,
  fallbackUrl: string | undefined,
  reducerPath: string,
) =>
  createApi({
    baseQuery: createGraphQLFetchBaseQuery(url, fallbackUrl, true, true),
    endpoints: () => ({}),
    reducerPath,
  });

const createBasePublicApi = (
  url: string,
  fallbackUrl: string,
  reducerPath: string,
) =>
  createApi({
    baseQuery: createGraphQLFetchBaseQuery(url, fallbackUrl, false, false),
    endpoints: () => ({}),
    reducerPath,
  });

export const baseAssetsApi = createBaseApi(
  API_URLS.ASSETS,
  LEGACY_API_URLS.ASSETS,
  'assets',
);
export const baseTelemetryApi = createBaseApi(
  API_URLS.TELEMETRY,
  LEGACY_API_URLS.TELEMETRY,
  'telemetry',
);
export const baseIntegrationApi = createBaseApi(
  API_URLS.INTEGRATIONS,
  LEGACY_API_URLS.INTEGRATIONS,
  'integrations',
);
export const baseIdentApi = createBaseApi(
  API_URLS.IDENT,
  LEGACY_API_URLS.IDENT,
  'ident',
);
export const baseAuditApi = createBaseApi(
  API_URLS.AUDIT,
  LEGACY_API_URLS.AUDIT,
  'audit',
);
export const baseJobsApi = createBaseApi(
  API_URLS.JOBS,
  LEGACY_API_URLS.JOBS,
  'jobs',
);
export const baseIdeaasApi = createBaseApi(
  API_URLS.IDEAAS,
  LEGACY_API_URLS.IDEAAS,
  'ideaas',
);
// A separate, parallel stack, so no legacy URL shape to fall back to.
export const baseIdeaasV2Api = createBaseApi(
  API_URLS.IDEAAS_V2,
  undefined,
  'ideaasV2',
);
export const baseVscodeApi = createBaseApi(
  API_URLS.VSCODE,
  LEGACY_API_URLS.VSCODE,
  'vscode',
);
export const baseOpenplcApi = createBaseApi(
  API_URLS.OPENPLC,
  LEGACY_API_URLS.OPENPLC,
  'openplc',
);
export const baseSdaiApi = createBaseApi(
  API_URLS.SDAI,
  LEGACY_API_URLS.SDAI,
  'sdai',
);
export const baseNotificationsApi = createBaseApi(
  API_URLS.NOTIFICATIONS,
  LEGACY_API_URLS.NOTIFICATIONS,
  'notifications',
);
export const baseOffboardingApi = createBaseApi(
  API_URLS.OFFBOARDING,
  LEGACY_API_URLS.OFFBOARDING,
  'offboarding',
);
export const baseBillingApi = createBaseApi(
  API_URLS.BILLING,
  LEGACY_API_URLS.BILLING,
  'billing',
);

export const baseIdentPublicApi = createBasePublicApi(
  API_URLS.IDENT_PUBLIC,
  LEGACY_API_URLS.IDENT_PUBLIC,
  'ident-public',
);
